import { sanitize } from "../../../lib/util/sanitizeDatatypes";
import psl from "psl";

export function getDomainForEmailAddress(emailAddress: string): string {
  // Do not throw, because this function is used in {UI code}
  return sanitize.hostname(emailAddress.split("@").pop(), "unknown");
}

/**
 * Returns the base domain of hostname.
 * E.g. for "www2.static.amazon.com" returns "amazon.com"
 * and for "www2.static.amazon.co.uk" returns "amazon.co.uk"
 */
export function getBaseDomainFromHost(hostname: string): string {
  return psl.get(hostname);
}

/**
 * Returns the base domain of a URL.
 * E.g. for "https://www2.static.amazon.co.uk/foo/bar.html"
 * returns "amazon.co.uk".
 * @throws in case the URL is malformed or is a
 * @throws for relative URLs like "/foo/bar.html"
 */
export function getBaseDomainFromURL(url: string): string {
  let hostname = new URL(url).hostname;
  return getBaseDomainFromHost(hostname);
}

/** Resolves once the network is online: at once if it already is, else when
 * the browser fires `online`, e.g. after the OS wakes from sleep. */
export function waitUntilOnline(): Promise<void> {
  return navigator.onLine
    ? Promise.resolve()
    : new Promise(resolve => addEventListener("online", () => resolve(), { once: true }));
}

/** Like `fetch()`'s `RequestInit`, plus an optional `timeout`. */
export interface FetchOptions extends RequestInit {
  /** Abort the request after this many milliseconds. */
  timeout?: number;
}

/** A non-2xx HTTP response. `httpCode` is the status, e.g. 404.
 * Read the response body on demand with `text()` or `json()` - most protocols
 * don't return a body for errors, so it isn't read unless you ask for it. */
export class HTTPError extends Error {
  readonly httpCode: number;
  readonly statusText: string;
  protected readonly response: Response;
  protected bodyText: Promise<string> | undefined;
  constructor(response: Response) {
    super(`HTTP ${response.status} ${response.statusText}`);
    this.httpCode = response.status;
    this.statusText = response.statusText;
    this.response = response;
  }

  /** The response body as text, read (once) on demand. */
  async text(): Promise<string> {
    this.bodyText ??= this.response.text();
    return await this.bodyText;
  }

  /** The response body parsed as JSON, or undefined if it isn't valid JSON. */
  async json(): Promise<any> {
    try {
      return JSON.parse(await this.text());
    } catch (ex) {
      return undefined;
    }
  }
}

/** Like `fetch()`, but returns the parsed JSON body.
 * @throws HTTPError if the response status is not 2xx */
export async function fetchJSON(url: RequestInfo | URL, options?: FetchOptions): Promise<any> {
  let response = await fetchChecked(url, options);
  return await response.json();
}

/** Like `fetch()`, but returns the response body as a string.
 * @throws HTTPError if the response status is not 2xx */
export async function fetchText(url: RequestInfo | URL, options?: FetchOptions): Promise<string> {
  let response = await fetchChecked(url, options);
  return await response.text();
}

async function fetchChecked(url: RequestInfo | URL, options?: FetchOptions): Promise<Response> {
  let { timeout, ...init } = options ?? {};
  if (timeout) {
    init.signal = init.signal
      ? AbortSignal.any([init.signal, AbortSignal.timeout(timeout)])
      : AbortSignal.timeout(timeout);
  }
  let response = await fetch(url, init);
  if (!response.ok) {
    throw new HTTPError(response);
  }
  return response;
}
